import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { ClientContractGrid, ContentWrapperBox, StyledButtonGrid, SubHeadingWrapper, SwitchGrid } from './ReportSettingsFragment.styles';
import { ReportSettingsErrorType, ReportSettingsFragmentProps, ReportSettingsType } from './ReportSettingsFragment.props';
import { Button, ButtonVariantProps } from '../../../../../components/atoms/Button';
import { FormControlLabel, FormControlLabelPlacementProps } from '../../../../../components/atoms/FormControlLabel';
import { Switch } from '../../../../../components/atoms/Switch';
import { ActionPermissions, ColorProps, NodeType } from '../../../../../@types';
import { ConfirmationPopup } from '../../../../../components/molecules/ConfirmationPopup';
import { COLORS } from '../../../../../configs/colors';
import { TypographyFontWeightProps, TypographyVariantProps } from '../../../../../components/atoms/Typography/Typography.props';
import { SelectionButton } from '../../../../../components/molecules/SelectionButton';
import { DropdownItem } from '../../../../../components/atoms/SelectMenu';
import { useGetNodeChildrenByNodeType, useGetStructuresByTypeCategoryCodeAndTypeCode } from '../../../../../queries/structure-query';
import { StructureTypeCategory } from '../../../../../configs/enums';
import { REPORT_SETTINGS_DEFAULT_DATA, REPORT_SETTINGS_DEFAULT_ERROR_DATA } from '../../../../../constants/report';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { useGetReportByCode, useUpdateReportByCode } from '../../../../../queries/report-query';
import { Snackbar } from '../../../../../components/atoms/Snackbar';
import { BrandSelector } from '../../../../../components/molecules/BrandSelector';

export const ReportSettingsFragment: React.FC<ReportSettingsFragmentProps> = ({
  reportCode,
  permissions
}) => {
  const [reportSettings, setReportSettings] = useState<ReportSettingsType>(REPORT_SETTINGS_DEFAULT_DATA);
  const [errors, setErrors] = useState<ReportSettingsErrorType>(REPORT_SETTINGS_DEFAULT_ERROR_DATA);
  const [showObfuscateWarning, setShowObfuscateWarning] = useState(false);
  const [openClientContractBrandSelector, setOpenClientContractBrandSelector] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [queryNodeType, setQueryNodeType] = useState(NodeType.Client);
  const [clients, setClients] = useState<DropdownItem[]>([]);
  const [contracts, setContracts] = useState<DropdownItem[]>([]);
  const [brands, setBrands] = useState<DropdownItem[]>([]);
  const [showToast, setShowToast] = useState(false);

  const getReportByCode = useGetReportByCode(reportCode);
  const updateReportByCode = useUpdateReportByCode(reportCode);
  const getStructuresByTypeCategoryCodeAndTypeCode = useGetStructuresByTypeCategoryCodeAndTypeCode(StructureTypeCategory.System, NodeType.Client);
  const getNodeChildrenByNodeType = useGetNodeChildrenByNodeType(selectedParentId, queryNodeType);

  useEffect(() => {
    getReportByCode.refetch();
  }, []);

  useEffect(() => {
    if (getReportByCode.data) {
      setReportSettings(getReportByCode.data?.reportProperties || REPORT_SETTINGS_DEFAULT_DATA);
      if (getReportByCode.data?.reportProperties?.obfuscation) {
        setSelectedParentId(getReportByCode.data?.reportProperties.obfuscation?.selectedClients[0]);
        setQueryNodeType(NodeType.Contract);
        getNodeChildrenByNodeType.refetch();
      }
    }
  }, [getReportByCode.data]);

  useEffect(() => {
    if (getStructuresByTypeCategoryCodeAndTypeCode.data) {
      setClients(
        getStructuresByTypeCategoryCodeAndTypeCode.data.map((item) =>
          ({ value: item.id.toString(), label: item.name, disabled: !item.isActive })
        )
      );
    }
  }, [getStructuresByTypeCategoryCodeAndTypeCode.data]);

  useEffect(() => {
    if (selectedParentId && queryNodeType && selectedParentId !== 'any' && selectedParentId.split(',').length === 1) {
      getNodeChildrenByNodeType.refetch();
    }
  }, [selectedParentId, queryNodeType]);
  
  useEffect(() => {
    if (getNodeChildrenByNodeType.data) {
      mapData(getNodeChildrenByNodeType.data);
    }
  }, [getNodeChildrenByNodeType.data]);

  const mapData = (data: any) => {
    if (queryNodeType == NodeType.Contract) {
      setContracts(data.map((item: any) => ({ value: item.id.toString(), label: item.name, disabled: !item.isActive })));
      setSelectedParentId(reportSettings.obfuscation.selectedContracts[0]);
      setQueryNodeType(NodeType.Brand);
    } else if (queryNodeType == NodeType.Brand) {
      setBrands(data.map((item: any) => ({ value: item.id.toString(), label: item.name, disabled: !item.isActive })));
    }
  };

  const onChangeNodeType = (nodeType: NodeType, parentId: string) => {
    setSelectedParentId(parentId);
    setQueryNodeType(nodeType);
  };

  const handleSave = async() => {
    if (reportSettings.obfuscation.clientHierarchy === '') {
      setErrors({ hasNoClientHierarchy: true });
      return;
    }
    await updateReportByCode.mutateAsync({
      ...getReportByCode?.data,
      reportProperties: reportSettings
    });
    getReportByCode.refetch();
    setShowToast(true);
  };

  return (
    <>
      <ContentWrapperBox>
        {isUserHasPermission(ActionPermissions.Report_My_Reports_Report_Settings_Can_Obfuscate, permissions) &&
        <Grid>
          <SubHeadingWrapper variant={TypographyVariantProps.Subtitle1} fontWeight={TypographyFontWeightProps.Bold}>
            Report Obfuscation
          </SubHeadingWrapper>
          <SwitchGrid ml={2}>
            <FormControlLabel
              control={<Switch
                color={ColorProps.Success}
                onChange={(event) => {
                  setReportSettings({ ...reportSettings, obfuscation: { ...reportSettings.obfuscation, obfuscate: event.target.checked } });
                  event.target.checked && setShowObfuscateWarning(true);
                } }
                checked={reportSettings.obfuscation.obfuscate} />}
              label="Show ALL part locations but obfuscate non-Client location names"
              labelPlacement={FormControlLabelPlacementProps.End} />
          </SwitchGrid>
          <ClientContractGrid>
            <SelectionButton
              label="Client Selection *"
              error={errors.hasNoClientHierarchy}
              value={reportSettings.obfuscation.clientHierarchy}
              onClick={() => {
                setOpenClientContractBrandSelector(true);
                getStructuresByTypeCategoryCodeAndTypeCode.refetch();
              } } />
          </ClientContractGrid>
        </Grid>}
        <StyledButtonGrid>
          <Button variant={ButtonVariantProps.Primary} onClick={handleSave}>
            Save
          </Button>
        </StyledButtonGrid>
        <BrandSelector
          open={openClientContractBrandSelector}
          onChangeNodeType={onChangeNodeType}
          handleClose={() => setOpenClientContractBrandSelector(false)}
          handleParentSelection={(selectedParentId: string, parentHierarchy: string, selectedClientId?: string, selectedContractId?: string) => {
            setOpenClientContractBrandSelector(false);
            parentHierarchy && errors.hasNoClientHierarchy && setErrors({ hasNoClientHierarchy: false });
            setReportSettings({
              ...reportSettings,
              obfuscation: {
                ...reportSettings.obfuscation,
                clientHierarchy: parentHierarchy,
                selectedClients: [selectedClientId as string],
                selectedContracts: [selectedContractId as string],
                selectedBrands: [selectedParentId]
              }
            });
          }}
          isActiveClear={false}
          clients={clients}
          contracts={contracts}
          brands={brands}
          clientId={parseInt(reportSettings.obfuscation.selectedClients[0])}
          contractId={parseInt(reportSettings.obfuscation.selectedContracts[0])}
          brandId={parseInt(reportSettings.obfuscation.selectedBrands[0])}
          pageType="report-settings"
        />
        <ConfirmationPopup
          open={showObfuscateWarning}
          mainHeader="Warning"
          description="Confirm report parameters are correct prior to sending to any external parties. Enabling this option may allow for visibility of locations outside the client's hierarchy."
          leftButtonName="Cancel"
          rightButtonName="Confirm"
          borderColor={COLORS.Red}
          leftButtonOnClick={() => {
            setReportSettings({ ...reportSettings, obfuscation: { ...reportSettings.obfuscation, obfuscate: false } });
            setShowObfuscateWarning(false);
          } }
          rightButtonOnClick={() => {
            setReportSettings({ ...reportSettings, obfuscation: { ...reportSettings.obfuscation, obfuscate: true } });
            setShowObfuscateWarning(false);
          } }
          leftButtonVariant={ButtonVariantProps.Secondary} />
      </ContentWrapperBox>
      <Snackbar
        open={showToast}
        message="Successfully saved"
        autoHideDuration={3000}
        onClose={() => { setShowToast(false); } }
      />
    </>
  );
};